// One login for both sites (nummius.com and academy.nummius.com). The app keeps its session in localStorage, which each
// site has its own copy of, so the session is mirrored into a cookie both sites can read (Domain=.nummius.com; host-only
// on dev, where both sites share 127.0.0.1 and cookies ignore the port). Same reach as localStorage (readable by the
// page, never used by the server: the API still takes the Bearer header), so nothing new is exposed.
//
// installSharedSession() runs once before the app renders:
//  - adopts the cookie's session when it differs from this site's (logged in on the other site),
//  - drops this site's session when the cookie is gone after it was shared (logged out on the other site),
//  - mirrors every later write or removal of the session keys into the cookie (Login, Register, logout, 401s...).

const COOKIE = 'nm_session';
const SHARED_FLAG = 'nm.sessionShared'; // this site's session has been mirrored at least once
const KEYS = { t: 'token', n: 'userName', e: 'userEmail', r: 'userRole' };
const MAX_AGE = 30 * 24 * 3600; // backend SESSION_TOKEN_TTL

let nativeSet = null;
let nativeRemove = null;

function cookieDomain() {
  const h = window.location.hostname;
  return h === 'nummius.com' || h.endsWith('.nummius.com') ? '; Domain=.nummius.com' : '';
}

function writeCookie(value, maxAge) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${cookieDomain()}${secure}`;
}

export function readSharedSession() {
  const m = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE}=`));
  if (!m) return null;
  try {
    const s = JSON.parse(decodeURIComponent(m.slice(COOKIE.length + 1)));
    return s && typeof s.t === 'string' && s.t ? s : null;
  } catch (_) {
    return null;
  }
}

function snapshot() {
  const s = {};
  Object.entries(KEYS).forEach(([k, key]) => { const v = localStorage.getItem(key); if (v != null) s[k] = v; });
  return s;
}

function mirror() {
  const s = snapshot();
  if (s.t) {
    writeCookie(encodeURIComponent(JSON.stringify(s)), MAX_AGE);
    nativeSet.call(localStorage, SHARED_FLAG, '1');
  } else {
    writeCookie('', 0);
  }
}

/** Sync this site's session with the shared cookie, then keep the cookie in step. Safe to call more than once. */
export function installSharedSession() {
  if (typeof window === 'undefined' || nativeSet) return;
  try {
    const proto = Object.getPrototypeOf(window.localStorage);
    nativeSet = proto.setItem;
    nativeRemove = proto.removeItem;
    const shared = readSharedSession();
    const mine = localStorage.getItem(KEYS.t);
    if (shared && shared.t !== mine) {
      Object.entries(KEYS).forEach(([k, key]) => (shared[k] != null ? nativeSet.call(localStorage, key, shared[k]) : nativeRemove.call(localStorage, key)));
    } else if (!shared && mine && localStorage.getItem(SHARED_FLAG) === '1') {
      Object.values(KEYS).forEach((key) => nativeRemove.call(localStorage, key)); // logged out on the other site
    }
    if (localStorage.getItem(KEYS.t)) mirror(); // refresh the cookie's lifetime
    const watched = new Set(Object.values(KEYS));
    // Patch the prototype (assigning localStorage.setItem would store an item named "setItem").
    proto.setItem = function setItem(key, value) {
      nativeSet.call(this, key, value);
      if (this === window.localStorage && watched.has(key)) mirror();
    };
    proto.removeItem = function removeItem(key) {
      nativeRemove.call(this, key);
      if (this === window.localStorage && watched.has(key)) mirror();
    };
  } catch (_) {
    // storage blocked (private mode, sandboxed preview): each site keeps its own login
  }
}
